import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Bot,
  Sparkles,
  Target,
  Coins,
  FileText,
  MessageCircle,
  TrendingUp,
  Compass,
  AlertTriangle,
  Building2,
  ShieldCheck,
  Heart,
  Wand2,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Briefcase,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import HeroTemplateCandidats from '../../components/sections/HeroTemplateCandidats';
import TalentChat from '../../components/chat/TalentChat';
import { WebPageSchema, BreadcrumbSchema, FAQPageSchema } from '../../lib/utils/schema-utils';

// — FAQ Schema (J+15 SEO push : capter long-tail + rich snippets Google) —
const talentAIFaqs = [
  {
    question: "TalentAI est-il vraiment gratuit ?",
    answer:
      "Oui. TalentAI est 100 % gratuit, sans formulaire, sans création de compte, sans carte bancaire. Vous l'utilisez directement dans votre navigateur. Aucune limite de questions, aucun paywall. Le modèle économique de recruteurs.io repose sur ses missions de recrutement payées par les entreprises — pas sur les candidats.",
  },
  {
    question: "Quelle est la différence entre TalentAI et ChatGPT pour ma recherche d'emploi ?",
    answer:
      "ChatGPT donne des conseils carrière génériques, tirés du web mondial. TalentAI a été construit par un cabinet de recrutement basé à Marseille : il connaît les fourchettes de salaire PACA, les codes des entretiens locaux, les attentes des recruteurs régionaux. Ses réponses portent le vécu de centaines d'entretiens et négociations réelles — pas seulement de la théorie carrière.",
  },
  {
    question: "TalentAI peut-il m'aider à rédiger mon CV et ma lettre de motivation ?",
    answer:
      "Oui. TalentAI vous accompagne sur la structure du CV (priorisation, formats ATS-friendly, mots-clés), la rédaction des expériences en mode résultats, et la lettre de motivation contextualisée à l'offre. Vous pouvez lui soumettre votre CV brut et lui demander de l'optimiser pour un poste précis.",
  },
  {
    question: "Comment préparer un entretien avec TalentAI ?",
    answer:
      "Vous lui donnez le nom de l'entreprise, l'intitulé du poste et la date de l'entretien. TalentAI vous prépare : questions probables du recruteur, points à mettre en avant, pièges classiques à éviter, exemples de réponses STAR. Vous pouvez simuler un entretien complet en mode question-réponse.",
  },
  {
    question: "TalentAI peut-il négocier mon salaire à ma place ?",
    answer:
      "Non, la négociation se fait en direct avec le recruteur. Mais TalentAI vous prépare : fourchettes marché PACA pour votre profil, leviers de négociation (variable, congés, télétravail, formation), formulations à utiliser, erreurs à éviter. Vous arrivez en entretien avec un plan, pas une espérance.",
  },
  {
    question: "Mes échanges avec TalentAI sont-ils confidentiels ?",
    answer:
      "Oui. Vos conversations ne sont pas associées à votre identité (pas de compte requis), ne sont jamais partagées avec les entreprises clientes de recruteurs.io, et ne servent pas à constituer un dossier sur vous. Si une opportunité parmi nos offres correspond à votre profil pendant l'échange, l'assistant vous le signalera — mais la décision de candidater reste 100 % la vôtre.",
  },
  {
    question: "TalentAI est-il adapté à tous les métiers ?",
    answer:
      "TalentAI est particulièrement pertinent sur les postes cadres et qualifiés (commerciaux, marketing, finance, RH, IT, ingénierie, retail, management). Sur les métiers très techniques ou très réglementés (médecine, droit, BTP spécialisé), ses conseils restent utiles sur la partie process (CV, entretien, négociation) mais moins fins sur le contenu métier.",
  },
  {
    question: "Sur quel marché géographique TalentAI est-il le plus précis ?",
    answer:
      "TalentAI est calibré sur le marché PACA (Marseille, Aix-en-Provence, Toulon, Nice, Avignon) et plus largement le grand Sud. Pour les recherches Paris/Lyon/Lille, les conseils carrière restent valables mais les fourchettes salariales et codes locaux peuvent être moins précis.",
  },
];

// — Typography aligned on candidat / pourquoi / decouvrir —
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass = 'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Section 2 — Constat candidat (3 pain points) —
const painPoints = [
  {
    icon: AlertTriangle,
    title: "Vous cherchez un poste avec ChatGPT générique. Il ne connaît pas votre marché.",
    description:
      "Une lettre de motivation calibrée pour la moyenne mondiale ne vous donne aucun avantage face à un recruteur PACA. Vous avez besoin d'un coach qui sait ce qui se pratique vraiment près de chez vous.",
  },
  {
    icon: Building2,
    title: "Vous n'avez pas de coach carrière, et un cabinet d'outplacement coûte 3 000 €",
    description:
      "Personne pour relire votre CV, préparer vos entretiens, négocier votre package. Vous improvisez à chaque étape — alors que les bons réflexes sont structurables.",
  },
  {
    icon: Bot,
    title: "Vous voulez préparer vos entretiens, pas tout réinventer à chaque candidature",
    description:
      "Chaque process est différent : grille de questions, package proposé, attentes implicites. TalentAI s'adapte à VOTRE candidature et vous fait gagner des heures par poste.",
  },
];

// — Section 3 — 6 domaines couverts par TalentAI —
const domains = [
  {
    icon: TrendingUp,
    title: 'Lecture du marché',
    description:
      "Salaires pratiqués pour votre métier sur Marseille / Aix / PACA, secteurs qui recrutent, dynamique régionale, métiers en tension.",
  },
  {
    icon: FileText,
    title: 'CV et lettre de motivation',
    description:
      "Restructuration de votre CV, optimisation pour les ATS, lettre de motivation contextualisée à l'offre. Pas du copier-coller — du sur-mesure.",
  },
  {
    icon: MessageCircle,
    title: 'Préparation entretien',
    description:
      "Questions types par fonction, exercices STAR, points faibles à anticiper, comment poser les bonnes questions au recruteur en retour.",
  },
  {
    icon: Coins,
    title: 'Négociation salariale',
    description:
      "Calibrage de votre fourchette, comment annoncer une prétention, contre-offres, package au-delà du salaire (variable, RTT, télétravail).",
  },
  {
    icon: Compass,
    title: 'Stratégie de carrière',
    description:
      "Orientation, reconversion, montée en compétences, choix entre 2 offres. Une boussole pour les décisions de moyen terme — pas juste la prochaine candidature.",
  },
  {
    icon: Target,
    title: 'Recherche active',
    description:
      "Quels canaux activer (job boards, LinkedIn, candidature spontanée), comment networker en PACA, comment identifier les recruteurs internes.",
  },
];

// — Section 4 — Comment ça marche (3 étapes) —
const howItWorks = [
  {
    icon: Bot,
    step: '01',
    title: 'Vous ouvrez le chatbot ci-dessous',
    description:
      "Aucun formulaire, aucune création de compte. Le coach s'ouvre directement dans votre navigateur — vous pouvez démarrer en 2 secondes.",
  },
  {
    icon: MessageCircle,
    step: '02',
    title: 'Vous posez votre question dans votre langage',
    description:
      "Pas de prompt à apprendre. Décrivez votre situation comme à un ami : votre métier, votre niveau, votre objectif, ce qui vous bloque aujourd'hui.",
  },
  {
    icon: Wand2,
    step: '03',
    title: 'Vous obtenez une réponse activable, contextualisée PACA',
    description:
      "Pas du blabla générique — des plans d'action structurés, des exemples concrets, des fourchettes salariales calibrées sur votre marché et votre niveau.",
  },
];

// — Section 6 — Différenciateurs (4 raisons) —
const differentiators = [
  {
    icon: ShieldCheck,
    title: 'Construit par un cabinet, pas par un éditeur SaaS',
    description:
      "Notre coach a été nourri par des centaines d'entretiens passés et des dizaines de négociations menées. Il restitue les vrais réflexes des recruteurs — pas du contenu corporate.",
  },
  {
    icon: TrendingUp,
    title: 'Calibré sur le marché PACA, pas sur la moyenne mondiale',
    description:
      "Les salaires proposés à Marseille ne sont pas ceux de Paris. Les attentes culturelles non plus. TalentAI le sait et adapte ses conseils en conséquence.",
  },
  {
    icon: Heart,
    title: '100 % gratuit, sans formulaire, sans création de compte',
    description:
      "Pas de paywall, pas d'email à donner, aucun engagement. On préfère que vous repartiez avec une vraie aide — quitte à ne jamais nous recontacter.",
  },
  {
    icon: Sparkles,
    title: 'Le pont vers nos offres réelles',
    description:
      "Si une offre recruteurs.io correspond à votre profil pendant votre échange, vous le verrez. Pas de pub forcée — juste une passerelle naturelle.",
  },
];

export default function TalentAIPage() {
  const navigate = useNavigate();

  return (
    <Layout
      title="TalentAI · Coach IA gratuit candidat (CV, entretien, salaire) | recruteurs.io"
      description="CV, lettre, entretien, négo salariale : coach IA 24/7 gratuit, sans inscription. Construit par un cabinet de recrutement Marseille, calibré marché PACA. Sans formulaire."
      keywords="coach ia candidat, talent ai, talentai, talent ai gratuit, assistant recherche emploi, ia pour candidats, préparation entretien ia, négociation salariale, cv ia, marché emploi marseille, recruteurs.io"
    >
      <WebPageSchema
        page={{
          title:
            'TalentAI · Coach IA gratuit pour candidats | recruteurs.io',
          description:
            "TalentAI : coach IA gratuit pour candidats. Lecture du marché PACA, CV, lettre de motivation, entretien, négociation, stratégie carrière. Construit par recruteurs.io.",
          url: 'https://recruteurs.io/candidat/talentai',
          lastReviewed: '2026-05-10',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Candidat', url: 'https://recruteurs.io/candidat/pourquoi' },
          {
            name: 'TalentAI',
            url: 'https://recruteurs.io/candidat/talentai',
          },
        ]}
      />

      {/* FAQ Schema — rich snippets Google + capter long-tail "talent ai gratuit", "ia recherche emploi", "coach ia entretien" */}
      <FAQPageSchema questions={talentAIFaqs} />

      <HeroTemplateCandidats
        page="candidat-talentai"
        title="Votre coach IA pour réussir votre recherche d'emploi."
        eyebrow="TalentAI · Assistant IA gratuit · Marseille · Aix · PACA"
        description="Lecture du marché, CV, lettre de motivation, préparation entretien, négociation, stratégie de carrière. TalentAI répond à vos questions de candidat avec le réflexe d'un cabinet de recrutement — gratuit, contextualisé, sans formulaire ni création de compte."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/pourquoi-recruteurs.io.webp"
        breadcrumbs={[
          { name: 'Candidat', href: '/candidat/pourquoi' },
          { name: 'TalentAI', href: '/candidat/talentai' },
        ]}
        cta1={{
          label: 'Démarrer avec TalentAI',
          href: '#talentai-chat',
        }}
        cta2={{
          label: 'Voir les sujets couverts',
          href: '#talentai-domains',
        }}
      />

      {/* SECTION 2 — Constat (3 pain points candidat) */}
      <SectionBackground page="candidat-talentai" section="cta-pain" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce que vit un candidat aujourd'hui</span>
            <h2 className={h2Class}>
              Vous cherchez un job{' '}
              <span className={gradientText}>sans coach et sans donnée marché.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Trois symptômes qu'on retrouve chez 90 % des candidats que nous
              recevons en entretien — et que TalentAI corrige dès la première
              question.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {painPoints.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="rounded-[1.75rem] bg-white p-7 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — 6 domaines couverts */}
      <SectionBackground
        page="candidat-talentai"
        section="cta-domains"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div
            id="talentai-domains"
            className="max-w-4xl mx-auto text-center mb-14 scroll-mt-24"
          >
            <span className={eyebrowClass}>6 sujets, 1 coach</span>
            <h2 className={h2Class}>
              Tout ce qu'un cabinet d'outplacement vous facturerait,{' '}
              <span className={gradientText}>en accès libre.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Six domaines de la recherche d'emploi couverts par défaut — avec
              contextualisation sur votre métier, votre niveau, votre marché.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {domains.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="rounded-[1.75rem] bg-white p-7 ring-1 ring-neutral-200 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-md shadow-[#d03484]/20">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Comment ça marche */}
      <SectionBackground
        page="candidat-talentai"
        section="cta-how"
        className="bg-white"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Comment ça marche</span>
            <h2 className={h2Class}>
              Trois étapes,{' '}
              <span className={gradientText}>aucun formulaire à remplir.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Vous ouvrez le chatbot, vous posez votre question, vous obtenez
              une réponse activable. C'est tout.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {howItWorks.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="relative rounded-[1.75rem] bg-white p-7 ring-1 ring-neutral-200 shadow-sm"
                >
                  <span
                    className={`absolute top-6 right-6 text-3xl font-bold ${gradientText} leading-none`}
                  >
                    {item.step}
                  </span>
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Chatbase iframe (accès libre, pas de gate) */}
      <SectionBackground
        page="candidat-talentai"
        section="cta-chat"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div
            id="talentai-chat"
            className="max-w-4xl mx-auto scroll-mt-24"
          >
            <div className="text-center mb-10">
              <span className={eyebrowClass}>
                <Bot className="w-4 h-4 mr-2" />
                Coach actif
              </span>
              <h2 className={h2Class}>
                Posez votre première question.{' '}
                <span className={gradientText}>Maintenant.</span>
              </h2>
              <p className={sectionSubtitleClass}>
                Démarrez par votre sujet prioritaire — l'assistant adaptera
                ses réponses à votre situation. Aucun formulaire à remplir.
              </p>
            </div>

            <TalentChat
              chatbotId="B-yCETKMIBROl19MyTnEz"
              welcomeMessage="Bonjour ! Je suis TalentAI, votre coach IA gratuit pour la recherche d'emploi. Posez-moi votre question — sur le marché PACA, votre CV, votre prochain entretien, votre négociation salariale ou votre stratégie de carrière."
              placeholder="Ex. : « Quel salaire viser pour un poste de chef de projet à Marseille ? »"
              quickPrompts={[
                'Calibre mon salaire pour mon métier à Marseille',
                "Aide-moi à structurer mon CV pour un poste de cadre",
                'Comment me préparer à un entretien chez un cabinet de conseil ?',
                'Quels sont les secteurs qui recrutent en PACA en ce moment ?',
              ]}
              height={640}
            />

            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-500">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-[#d03484]" /> 100 % gratuit
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-[#d03484]" /> Sans création de compte
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-[#d03484]" /> Aucun engagement
              </span>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Différenciateurs */}
      <SectionBackground
        page="candidat-talentai"
        section="cta-diff"
        className="bg-white"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Pourquoi celui-ci, pas ChatGPT</span>
            <h2 className={h2Class}>
              ChatGPT répond à tout le monde.{' '}
              <span className={gradientText}>TalentAI répond à vous.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Quatre choses qui distinguent un coach IA construit par un
              cabinet de recrutement d'un assistant générique grand public.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="flex gap-4 rounded-[1.75rem] bg-white p-7 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-md shadow-[#d03484]/20">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className={h3Class}>{item.title}</h3>
                    <p className={bodyClass}>{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 7 — Bridge vers offres et Parcours Découverte */}
      <SectionBackground
        page="candidat-talentai"
        section="cta-bridge"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-4xl mx-auto rounded-[2rem] bg-gradient-to-br from-[#30475e] to-[#1f3245] p-10 md:p-12 text-white shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-4 h-4 text-[#fde7f2]" />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#fde7f2]">
                Et après le coach ?
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-5 leading-[1.1]">
              Vous êtes prêt·e à candidater{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fde7f2] to-[#fde2d3]">
                en connaissance de cause
              </span>{' '}
              ?
            </h2>
            <p className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-3xl">
              TalentAI vous a aidé à structurer votre démarche. Maintenant,
              passez à l'action : explorez nos offres en cours, découvrez
              l'entreprise avant de signer, ou lisez nos engagements de cabinet
              côté candidat.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/offres')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-2 text-sm font-bold whitespace-nowrap text-[#30475e] hover:bg-[#fde7f2] hover:-translate-y-0.5 transition-all shadow-lg"
              >
                Voir les offres en cours
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/candidat/decouvrir')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl ring-1 ring-white/30 bg-white/10 backdrop-blur-sm px-5 py-2 text-sm font-semibold whitespace-nowrap text-white hover:bg-white/20 transition-all"
              >
                Découvrir avant de signer
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 8 — CTA final */}
      <SectionBackground
        page="candidat-talentai"
        section="cta-cta"
        className="bg-white"
      >
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <Sparkles className="w-10 h-10 text-[#d03484] mx-auto mb-5" />
            <h2 className={h2Class}>
              Le recrutement traité avec respect.{' '}
              <span className={gradientText}>Votre recherche aussi.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-8`}>
              Démarrez avec TalentAI maintenant. Si plus tard vous voulez parler
              à un humain de notre cabinet, nous restons à un appel.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="#talentai-chat"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Démarrer avec TalentAI
                <ArrowRight className="w-4 h-4" />
              </a>
              <button
                onClick={() => navigate('/candidat/pourquoi')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl ring-1 ring-neutral-200 bg-white px-5 py-2 text-sm font-semibold whitespace-nowrap text-[#30475e] hover:bg-[#fbfaf8] transition-all"
              >
                Nos engagements candidats
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 9 — SEO content */}
      <SectionBackground
        page="candidat-talentai"
        section="cta-seo"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-3xl mx-auto prose prose-neutral">
            <h2 className={`${h2Class} text-3xl md:text-4xl`}>
              TalentAI en quelques mots
            </h2>
            <p className={bodyClass}>
              <strong>TalentAI</strong> est l'assistant IA conçu par{' '}
              <strong>recruteurs.io</strong>, cabinet de recrutement basé à
              Marseille, pour les candidats qui veulent structurer leur
              recherche d'emploi sans payer un cabinet d'outplacement.
            </p>
            <p className={`${bodyClass} mt-4`}>
              Là où un assistant grand public restitue des conseils génériques
              tirés de l'ensemble du web, TalentAI a été nourri par la pratique
              d'un cabinet : centaines d'entretiens passés, dizaines de
              négociations menées, lecture quotidienne du marché PACA. Ses
              réponses portent ce vécu — pas juste de la théorie carrière.
            </p>
            <p className={`${bodyClass} mt-4`}>
              Six domaines sont couverts par défaut : la lecture du marché de
              l'emploi (salaires, secteurs, métiers en tension), la rédaction
              du CV et de la lettre de motivation, la préparation aux
              entretiens, la négociation salariale, la stratégie de carrière et
              la recherche active. La contextualisation tient compte de votre
              métier, votre niveau et lorsque c'est pertinent, du marché PACA
              (Marseille, Aix-en-Provence, Toulon, Nice).
            </p>
            <p className={`${bodyClass} mt-4`}>
              L'usage est <strong>100 % gratuit, sans formulaire et sans
              création de compte</strong>. Si une opportunité parmi nos{' '}
              <a
                href="/offres"
                className="text-[#d03484] hover:underline font-semibold"
              >
                offres en cours
              </a>{' '}
              correspond à votre profil pendant votre échange, l'assistant
              vous l'indiquera — mais il n'y a aucune obligation à candidater
              pour utiliser le coach.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* — FAQ visible — texte HTML pour Google + UX. Schema FAQ ci-dessus pour rich snippets. — */}
      <SectionBackground page="candidat-talentai" section="faq" className="bg-neutral-50">
        <Section>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className={eyebrowClass}>FAQ TalentAI</span>
              <h2 className={h2Class}>
                Vos questions sur le <span className={gradientText}>coach IA candidat</span>.
              </h2>
              <p className={sectionSubtitleClass}>
                Avant de démarrer, voici ce que vous demandez le plus souvent.
              </p>
            </div>
            <dl className="space-y-6">
              {talentAIFaqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-6 md:p-8 shadow-sm ring-1 ring-neutral-200"
                >
                  <dt className={`${h3Class} mb-3`}>{faq.question}</dt>
                  <dd className={bodyClass}>{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
